import * as React from 'react'
import {
  Activity,
  Cloud,
  Database,
  FileCode,
  FileSpreadsheet,
  FileText,
  Network,
  Plug,
  Search,
  ShieldCheck,
  Table,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ConnectorManifest } from '../connectorRegistry'

function resolveIconSource(iconRef: string): string {
  if (
    iconRef.startsWith('/') ||
    iconRef.startsWith('data:') ||
    iconRef.startsWith('http://') ||
    iconRef.startsWith('https://')
  ) {
    return iconRef
  }
  return `/${iconRef}`
}

/**
 * Picks a sensible Lucide icon for a connector when no explicit `iconRef`
 * is registered. Derived from the manifest id + format so every connector
 * gets a meaningful glyph in the picker without forcing every domain to
 * register an asset.
 */
export const ConnectorIcon: React.FC<{
  manifest: ConnectorManifest
  className?: string
}> = ({ manifest, className }) => {
  const [imageFailed, setImageFailed] = React.useState(false)
  const id = manifest.id.toLowerCase()
  const fmt = manifest.format

  React.useEffect(() => {
    setImageFailed(false)
  }, [manifest.iconRef])

  if (manifest.iconRef && !imageFailed) {
    return (
      <img
        src={resolveIconSource(manifest.iconRef)}
        alt=""
        aria-hidden="true"
        className={cn('object-contain', className)}
        onError={() => setImageFailed(true)}
      />
    )
  }

  const Comp =
    id.includes('grc') || id.includes('control') || id.includes('register')
      ? ShieldCheck
      : id.includes('csv')
        ? Table
        : id.includes('xls') || id.includes('sheet') || id.includes('excel')
          ? FileSpreadsheet
          : id.includes('bpmn') || id.includes('xml')
            ? FileCode
            : id.includes('sop') || id.includes('pdf') || id.includes('doc')
              ? FileText
              : id.includes('aws') ||
                  id.includes('cloud') ||
                  id.includes('terraform') ||
                  id.includes('cf') ||
                  id.includes('tf')
                ? Cloud
                : id.includes('scan')
                  ? Search
                  : id.includes('network') || id.includes('infra')
                    ? Network
                    : fmt === 'live-scan'
                      ? Activity
                      : fmt === 'oauth' || fmt === 'api-key'
                        ? Plug
                        : fmt === 'file'
                          ? FileText
                          : Database

  return <Comp className={className} />
}
